/**
 * THE two-tier free-text match: does this ROW match what is in a search box?
 *
 * Extracted 2026-09-13 from `utils/orderSearch.js` when `utils/hireSearch.js`
 * needed the identical rule for a different row shape. `check-duplication`
 * scored the two predicates at 0.95 over 74 normalised tokens and blocked the
 * commit; per the Code Unification Principle the shared rule is extracted and
 * imported rather than declared as debt. **The BEHAVIOUR did not change** —
 * `orderSearch.test.js` is the proof, unedited across the extraction.
 *
 * ── THE TWO TIERS, AND WHY THEY MATCH DIFFERENTLY ───────────────────────────
 * Prose (a description, a supplier, a person's name) is matched as a
 * case-insensitive SUBSTRING: half a word is a legitimate query.
 *
 * An IDENTIFIER (`ORD-004`, `PO-147`) is matched EXACTLY over a fold that
 * ignores separators, case and leading zeros. It is quoted whole off a screen
 * or down a phone, so half of one is not a match — and the substring loop is
 * wrong in BOTH directions for a bare integer anyway: the stored `4` does not
 * contain the displayed `ORD-004`, and the query `4` would match 14, 40 and
 * 400.
 *
 * The tiers are OR'd, never chained: a query that is not an identifier falls
 * through to the text loop unchanged.
 *
 * Empty / whitespace-only queries match every row.
 */

/**
 * Fold an identifier to the characters a human would agree are "the same one":
 * drop every separator, case-fold, drop leading zeros from each digit run.
 *
 * 🔑 The leading-zero clause keeps PADDING a display decision: the screen shows
 * `ORD-004`, a colleague down a phone says "order four", and both fold to the
 * same token without this file knowing anything about `padStart`.
 *
 * 🪤 NOT a copy of `poNumber.js`'s rule and must not drift into one. That rule
 * decides what gets STORED and is strict; this is a READ-side leniency with
 * nothing persisted behind it, so `PO147` folds to find `PO-147` even though the
 * write side would store that spelling differently.
 */
const IDENTIFIER_SEPARATORS = /[^a-z0-9]+/g;
const LEADING_ZEROS_IN_RUN = /(\D|^)0+(\d)/g;

export function foldIdentifier(value) {
  if (value == null) return '';
  return String(value)
    .toLowerCase()
    .replace(IDENTIFIER_SEPARATORS, '')
    .replace(LEADING_ZEROS_IN_RUN, '$1$2');
}

/**
 * `spec` is the only thing a caller supplies, and each part is optional:
 *
 *   fields          — column names matched as a case-insensitive SUBSTRING.
 *   identifierTokens(row) → Set  — the folded strings that NAME this row,
 *                     tested for exact membership. Omit for a row with no
 *                     identifier.
 *   extraValues(row) → string[]  — DERIVED text that is not a column, matched
 *                     as a substring alongside `fields`. `hireSearch.js` uses
 *                     it for the hire-status LABEL the user can actually see.
 *
 * 🪤 Every field read is null-safe and coerced with `String(...)`: a schema
 * admits numbers where a caller expects text, and `null.toLowerCase()` is the
 * crash this guards.
 */
export function matchesTextSearch(row, searchTerm, spec) {
  if (!row) return false;
  const q = (searchTerm || '').trim().toLowerCase();
  if (!q) return true;

  // Identifier tier first — a small exact-set membership test.
  if (spec.identifierTokens) {
    const queryForm = foldIdentifier(searchTerm);
    if (queryForm && spec.identifierTokens(row).has(queryForm)) return true;
  }

  if (spec.extraValues) {
    for (const value of spec.extraValues(row)) {
      if (value == null) continue;
      if (String(value).toLowerCase().includes(q)) return true;
    }
  }

  for (const field of spec.fields || []) {
    const value = row[field];
    if (value == null) continue;
    // 🔑 SUBSTRING here, and load-bearing: it is why `reference_number` staying
    // searchable lets `ORD-120` still find `AURORA-ORD-120`. Do not narrow to
    // `===` or `startsWith`.
    if (String(value).toLowerCase().includes(q)) return true;
  }
  return false;
}
